import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  updateDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { fechaDeDia } from "./dates";
import {
  varianteDe,
  costoDeVariante,
  costoProveedorDeVariante,
  nombreConVariante,
} from "./catalogo";

const eventDocRef = (venueId, eventId) =>
  doc(db, "venues", venueId, "calendars", "default", "events", eventId);

// ── Categorías múltiples (2026-10) ──────────────────────────────────────
// Un proveedor puede estar en más de una categoría: `categorias` es una lista
// de `{ categoria, subcategoria }` en orden de captura. NO hay categoría
// principal: un planner que además renta mobiliario es las dos cosas por
// igual. `categoria` / `subcategoria` siguen existiendo en el doc y espejan
// la primera entrada SOLO por compatibilidad con los lectores viejos
// (asignaciones del evento, agrupación del cotizador, ficha del cliente);
// no significan prioridad. Los docs viejos solo traen el par; se leen como
// lista de uno. Sin migración: cualquier escritura pasa por
// camposCategorias() y deja las dos formas consistentes.
export const categoriasDe = (proveedor) => {
  const vistas = new Set();
  const limpia = (lista) =>
    lista
      .filter((e) => e && e.categoria && !vistas.has(e.categoria) && vistas.add(e.categoria))
      .map((e) => ({ categoria: e.categoria, subcategoria: e.subcategoria || null }));
  const lista = Array.isArray(proveedor?.categorias) ? limpia(proveedor.categorias) : [];
  if (lista.length) return lista;
  if (proveedor?.categoria) {
    return [{ categoria: proveedor.categoria, subcategoria: proveedor.subcategoria || null }];
  }
  return [];
};

// ── Roles de planner ──────────────────────────────────────────────────
// Un proveedor puede ser planner y además dar servicios. Hay tres preguntas
// distintas y cada lector debe hacerse la suya:
//   esPlannerProveedor → ¿puede ser EL planner de un evento y cobrar
//     comisión? (pestaña Planners, picker de planner, sección Comisión).
//   daServicios → ¿tiene algo que contratar aparte de planear? (pestaña
//     Proveedores, sección Servicios). Un proveedor sin categoría también.
//   esSoloPlanner → ¿lo ÚNICO que hace es planear? Solo entonces un
//     servicio suyo se trata como comisión y no genera costo de catálogo.
export const esPlannerProveedor = (p) =>
  !!p && (p.isPlanner === true || tieneCategoria(p, "planner"));

export const daServicios = (p) => {
  if (!p) return false;
  const cats = categoriasDe(p);
  if (cats.length === 0) return !esPlannerProveedor(p);
  return cats.some((e) => e.categoria !== "planner");
};

export const esSoloPlanner = (p) => esPlannerProveedor(p) && !daServicios(p);

export const tieneCategoria = (proveedor, categoriaId) =>
  !!categoriaId && categoriasDe(proveedor).some((e) => e.categoria === categoriaId);

export const tieneSubcategoria = (proveedor, subcategoriaId) =>
  !!subcategoriaId && categoriasDe(proveedor).some((e) => e.subcategoria === subcategoriaId);

// Convierte la lista editada en los campos a escribir en el doc. Quita filas
// vacías y categorías repetidas (gana la primera), espeja la primera entrada
// en `categoria`/`subcategoria` (compatibilidad, no prioridad) y deriva
// isPlanner: planner en CUALQUIER posición puede ser planner de un evento
// (comisión, picker de planner, pestaña Planners), sin dejar de ser
// proveedor por sus demás categorías.
export const camposCategorias = (lista) => {
  const vistas = new Set();
  const limpias = [];
  (Array.isArray(lista) ? lista : []).forEach((e) => {
    const cat = e?.categoria || null;
    if (!cat || vistas.has(cat)) return;
    vistas.add(cat);
    limpias.push({ categoria: cat, subcategoria: e?.subcategoria || null });
  });
  const principal = limpias[0] || null;
  return {
    categorias: limpias,
    categoria: principal?.categoria || null,
    subcategoria: principal?.subcategoria || null,
    isPlanner: limpias.some((e) => e.categoria === "planner"),
  };
};

// startTime llega como Timestamp, {seconds}, Date o string según la época del
// evento; el costo necesita un Timestamp real para que las rules lo acepten y
// para que Cuentas lo pueda anclar a su día calendario.
export const aTimestamp = (val) => {
  if (!val) return null;
  if (typeof val?.toDate === "function") return Timestamp.fromDate(val.toDate());
  if (typeof val?.seconds === "number")
    return new Timestamp(val.seconds, val.nanoseconds || 0);
  const d = val instanceof Date ? val : new Date(val);
  return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
};

// Un costo auto sigue "intacto" mientras nadie lo haya tocado: sigue siendo
// automático, sigue por pagar y su monto sigue siendo el que puso la tarifa.
// Solo entonces desasignar al proveedor lo puede borrar sin perder trabajo
// que alguien haya hecho encima.
export const esCostoAutoIntacto = (costo) =>
  !!costo &&
  costo.origen === "proveedor_auto" &&
  (costo.estado || "por_pagar") === "por_pagar" &&
  Number(costo.monto) === Number(costo.montoOriginal);

// Costo automático del SERVICIO contratado (FRA-349). La tarifa ya no vive
// en el proveedor —un proveedor da varios servicios con precios distintos—
// sino en el propio servicio (o en su variante): tarifa × cantidad. Es un
// SNAPSHOT: `monto` se congela aquí y cambiar la tarifa después no toca este
// doc (para eso está el tarifaHistorial del servicio).
async function crearCostoAutoDeProveedor({
  venueId,
  eventId,
  proveedor,
  servicio = null,
  varianteId = null,
  cantidad = 1,
  userId,
  eventData,
}) {
  // Sin servicio (o sin costo en él) el flujo queda como antes de esta
  // feature: la asignación existe, el costo se captura a mano. Sin uid las
  // rules rechazarían el create (createdBy debe ser el caller).
  if (!servicio?.id || !userId) return null;
  // Un planner NUNCA genera costo automático de servicio: cobra comisión, y
  // esa la registra registrarComisionDeEvento a partir de su acuerdo
  // (FRA-355). Valuarlo aquí con la tarifa del catálogo producía una segunda
  // comisión, con el monto equivocado y sin el snapshot del acuerdo.
  // Un planner que ADEMÁS da servicios sí genera costo por el servicio: lo
  // que se está contratando aquí es el servicio, no la planeación.
  if (esSoloPlanner(proveedor)) return null;
  const variante = varianteDe(servicio, varianteId);
  const cant = Number(cantidad) > 0 ? Number(cantidad) : 1;
  const monto = costoDeVariante(servicio, variante, cant);
  if (!(monto > 0)) return null;
  const docRef = await addDoc(collection(db, "venues", venueId, "costos"), {
    eventId,
    concepto: nombreConVariante(servicio, variante),
    categoria: "Proveedores",
    proveedorId: proveedor.id,
    proveedorNombre: proveedor.nombre || "",
    conceptoId: servicio.id,
    conceptoNombre: servicio.nombre || "",
    varianteId: variante?.id || null,
    cantidad: cant,
    // La tarifa con la que se valuó, congelada: cambiarla después no toca
    // este doc (para eso está el tarifaHistorial del servicio).
    costoUnitarioSnapshot: costoProveedorDeVariante(servicio, variante),
    monto,
    montoOriginal: monto,
    // El día del evento como "día calendario", no el instante (utils/dates.js).
    fecha: fechaDeDia(eventData?.startTime) || Timestamp.now(),
    estado: "por_pagar",
    fechaPago: null, // se captura el día que de verdad se pague (FRA-342)
    origen: "proveedor_auto",
    metodo: null,
    notas: null,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: null,
    updatedBy: null,
  });
  return { costoId: docRef.id, monto };
}

const buildAssignmentEntry = (proveedor, userId, servicio = null, varianteId = null, cantidad = 1) => ({
  proveedorId: proveedor.id,
  proveedorNombre: proveedor.nombre || "",
  categoria: proveedor.categoria || null,
  subcategoria: proveedor.subcategoria || null,
  // Qué servicio se le contrató (FRA-349). Null = asignación sin servicio.
  servicioId: servicio?.id || null,
  servicioNombre: servicio ? nombreConVariante(servicio, varianteDe(servicio, varianteId)) : null,
  varianteId: varianteId || null,
  cantidad: Number(cantidad) > 0 ? Number(cantidad) : 1,
  assignedAt: Timestamp.now(),
  assignedBy: userId || null,
});

// Assign a proveedor to an event. If proveedor is a planner, also sets
// plannerId/plannerNombre on the event doc.
//
// Si se contrata un servicio con estructura de costo, además cae su costo
// automático en el evento. El ORDEN importa: primero la asignación, después
// el costo. Si el costo falla, el proveedor queda bien asignado y el caller
// avisa; al revés dejaría un costo huérfano de una asignación que nunca
// ocurrió.
//
// Devuelve `{ costoId, monto }` si generó costo, o null si no.
export async function assignProveedorToEvent({
  venueId,
  eventId,
  proveedor,
  userId = null,
  servicio = null,
  varianteId = null,
  cantidad = 1,
}) {
  if (!venueId || !eventId || !proveedor?.id) return;
  const ref = eventDocRef(venueId, eventId);

  // `proveedoresAsignados` es un array dentro del doc del evento, así que
  // asignar es leer-modificar-escribir y dos personas asignando a la vez se
  // pisaban: la segunda escritura mandaba el array que leyó ANTES de la
  // primera y una de las dos asignaciones desaparecía sin ruido. En
  // transacción la segunda reintenta contra el estado ya actualizado.
  //
  // `arrayUnion` no sirve aquí: la entrada lleva `assignedAt` y el criterio
  // de duplicado es el proveedorId, no el objeto completo.
  const { data, yaEstaba } = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Event not found");
    const actual = snap.data();
    const existing = Array.isArray(actual.proveedoresAsignados)
      ? actual.proveedoresAsignados
      : [];
    if (existing.some((e) => e.proveedorId === proveedor.id)) {
      return { data: actual, yaEstaba: true };
    }
    const entry = buildAssignmentEntry(proveedor, userId, servicio, varianteId, cantidad);
    const patch = {
      proveedoresAsignados: [...existing, entry],
    };
    // Un planner puro asignado como proveedor cualquiera se vuelve EL planner
    // del evento solo si no había otro: si ya hay uno, sustituirlo en silencio
    // le cambiaría el planner al evento sin que nadie lo pidiera. Un planner
    // que además da servicios NO: se le está contratando el servicio, y el
    // rol de planner se asigna desde su propio picker.
    if (esSoloPlanner(proveedor) && !actual.plannerId) {
      patch.plannerId = proveedor.id;
      patch.plannerNombre = proveedor.nombre || "";
    }
    tx.update(ref, patch);
    return { data: actual, yaEstaba: false };
  });

  if (yaEstaba) return null; // already assigned — no duplica el costo tampoco

  // El costo va FUERA de la transacción y después, como antes: si falla, el
  // proveedor queda bien asignado y el caller avisa. Al revés dejaría un
  // costo huérfano de una asignación que nunca ocurrió.
  return crearCostoAutoDeProveedor({
    venueId,
    eventId,
    proveedor,
    servicio,
    varianteId,
    cantidad,
    userId,
    eventData: data,
  });
}

// Remove a proveedor from an event. If the proveedor was the planner, clears
// plannerId/plannerNombre too.
//
// `autoCosto` lo manda el caller (que ya trae los costos suscritos en el
// contexto, así no se paga una lectura extra). Si sigue intacto se borra
// junto con la asignación; si alguien lo editó o ya lo pagó se conserva y el
// caller lo avisa — nunca se tira trabajo del owner por desasignar.
export async function unassignProveedorFromEvent({
  venueId,
  eventId,
  proveedorId,
  autoCosto = null,
}) {
  if (!venueId || !eventId || !proveedorId) return { costoBorrado: false };
  const ref = eventDocRef(venueId, eventId);

  // En transacción por lo mismo que `assignProveedorToEvent`: el filtro se
  // aplica sobre el array que se acaba de leer, y sin transacción desasignar
  // a uno podía revivir —o borrar— la asignación que otra persona hizo en
  // paralelo.
  const existia = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return false;
    const data = snap.data();
    const existing = Array.isArray(data.proveedoresAsignados)
      ? data.proveedoresAsignados
      : [];
    const patch = {
      proveedoresAsignados: existing.filter(
        (e) => e.proveedorId !== proveedorId
      ),
    };
    if (data.plannerId === proveedorId) {
      patch.plannerId = null;
      patch.plannerNombre = null;
    }
    tx.update(ref, patch);
    return true;
  });
  if (!existia) return { costoBorrado: false };

  if (autoCosto?.id && esCostoAutoIntacto(autoCosto)) {
    await deleteDoc(doc(db, "venues", venueId, "costos", autoCosto.id));
    return { costoBorrado: true, costoConservado: false };
  }
  return { costoBorrado: false, costoConservado: !!autoCosto?.id };
}

// Set the planner shortcut on the event. Adds the proveedor to
// proveedoresAsignados if not already present.
export async function setPlannerOnEvent({
  venueId,
  eventId,
  proveedor,
  userId = null,
  servicio = null,
  varianteId = null,
  cantidad = 1,
}) {
  if (!venueId || !eventId || !proveedor?.id) return;
  const ref = eventDocRef(venueId, eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Event not found");
  const data = snap.data();
  const existing = Array.isArray(data.proveedoresAsignados)
    ? data.proveedoresAsignados
    : [];
  const alreadyAssigned = existing.some(
    (e) => e.proveedorId === proveedor.id
  );
  const patch = {
    plannerId: proveedor.id,
    plannerNombre: proveedor.nombre || "",
  };
  if (!alreadyAssigned) {
    patch.proveedoresAsignados = [
      ...existing,
      buildAssignmentEntry(proveedor, userId, servicio, varianteId, cantidad),
    ];
  }
  await updateDoc(ref, patch);
  // Un planner no cobra tarifa: cobra comisión (FRA-355). El costo lo
  // registra el caller con registrarComisionDeEvento a partir del acuerdo
  // del planner; aquí ya no se evalúa ningún servicio. Se conserva la firma
  // para los callers viejos, que reciben null.
  return null;
}

// Clear the planner shortcut. Leaves proveedoresAsignados intact so the venue
// can decide separately whether to keep them assigned in another role.
export async function clearPlannerFromEvent({ venueId, eventId }) {
  if (!venueId || !eventId) return;
  await updateDoc(eventDocRef(venueId, eventId), {
    plannerId: null,
    plannerNombre: null,
  });
}

// Recompute ratingPromedio + ratingCount on the proveedor doc by scanning its
// reviews subcollection. Called after every review create/edit/delete.
// Counts only reviews with a numeric rating (1-5). Reviews without a rating
// (comentario-only) are kept in the subcollection but excluded from the average.
export async function recomputeProveedorRatingAggregates(venueId, proveedorId) {
  if (!venueId || !proveedorId) return;
  const reviewsCol = collection(
    db,
    "venues",
    venueId,
    "proveedores",
    proveedorId,
    "reviews"
  );
  const snap = await getDocs(reviewsCol);
  const ratings = snap.docs
    .map((d) => d.data().rating)
    .filter((r) => typeof r === "number" && r >= 1 && r <= 5);
  const ratingCount = ratings.length;
  const ratingPromedio =
    ratingCount > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / ratingCount
      : null;
  const proveedorRef = doc(
    db,
    "venues",
    venueId,
    "proveedores",
    proveedorId
  );
  await updateDoc(proveedorRef, {
    ratingPromedio,
    ratingCount,
    updatedAt: serverTimestamp(),
  });
}
